import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { useStores } from '../../hooks/use-stores';
import { VisualEditor, buildEditorApiPayload } from '@awarego-ui/components';
import QuestionPreviewModal from '../../components/manager/assessment/QuestionPreviewModal';
import services from '../../services';
import { extractErrorMessage } from '../../utils/helpers';
import {
    FlexDiv,
    Block,
    Heading3,
    ThemedButton,
    TextDefault,
} from '@awarego-ui/components';
import { Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThemedSpinner from '../../components/themed/ThemedSpinner';

function parseQuestionDefinition(definition) {
    if (definition == null) return {};
    if (typeof definition === 'string') {
        try {
            return JSON.parse(definition);
        } catch {
            return {};
        }
    }
    return typeof definition === 'object' ? { ...definition } : {};
}

function mapApiQuestionToEditor(api) {
    const def = parseQuestionDefinition(api.definition);
    const ui = api.ui || def.ui || {
        blocks: [],
        presentation: null,
        minimumBlockCount: 1,
    };
    return {
        id: api.id,
        unique_id: api.unique_id,
        owner_unique_id: api.owner_unique_id ?? null,
        name: api.name || 'New Question',
        description:
            api.description ||
            api.adminDescription ||
            def.adminDescription ||
            '',
        area: api.area || def.area || 'General',
        difficulty: api.difficulty || def.difficulty || 'basic',
        behaviours: api.behaviours || def.behaviours || [],
        languages: api.languages || def.languages || ['en'],
        tags: api.tags || def.tags || [],
        actions: api.actions ?? def.actions,
        translations: api.translations ?? def.translations,
        type: api.type ?? def.type,
        personalizedKeysUsed: api.personalizedKeysUsed,
        personalizedKeysActions: api.personalizedKeysActions,
        max_score: api.max_score ?? def.max_score,
        components: api.components ?? def.components ?? 'Behaviour',
        ui: {
            blocks: ui.blocks || [],
            presentation: ui.presentation || null,
            minimumBlockCount: ui.minimumBlockCount ?? 1,
        },
    };
}

function QuestionEditor() {
    const { store, brandingStore, resourcesStore, catalogStore } = useStores();
    const primaryColor = brandingStore?.theme?.primary;
    const { currentCompany } = store;
    const { questionsEditorVisible } = brandingStore;
    const navigate = useNavigate();
    const { questionId } = useParams();

    const [pageLoading, setPageLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [initialQuestion, setInitialQuestion] = useState(null);
    const [loadVersion, setLoadVersion] = useState(0);

    const isEditing = Boolean(questionId);
    const companyId = currentCompany?.company_id;
    const companyUid = currentCompany?.company_uid;
    const accountUid = currentCompany?.account_uid;

    const loadQuestion = useCallback(async () => {
        if (!questionId || !companyId) {
            setInitialQuestion(null);
            return;
        }
        setLoadError(null);
        setPageLoading(true);
        try {
            const data = await services.Companies.questionsService(
                companyId
            ).fetch(questionId);
            setInitialQuestion(mapApiQuestionToEditor(data));
            setLoadVersion((v) => v + 1);
        } catch (err) {
            setLoadError(
                extractErrorMessage(err) ||
                    'Failed to load question. Please try again.'
            );
            setInitialQuestion(null);
        } finally {
            setPageLoading(false);
        }
    }, [questionId, companyId]);

    useEffect(() => {
        if (isEditing) {
            loadQuestion();
        } else {
            setInitialQuestion(null);
            setLoadError(null);
            setLoadVersion((v) => v + 1);
        }
    }, [isEditing, loadQuestion]);

    useEffect(() => {
        if (!companyId) return;
        catalogStore.loadThreatAreaCatalog(companyId);
    }, [companyId, catalogStore]);

    const handleSave = async (question) => {
        if (!companyId) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        const body = buildEditorApiPayload(question);
        try {
            if (question.id) {
                await services.Companies.questionsService(
                    companyId
                ).companyEditorUpdate(question.id, body);
                setSuccess('Question updated successfully!');
                await resourcesStore.loadAllContent(companyId);
                setTimeout(() => navigate('/content'), 1200);
            } else {
                const { id } = await services.Companies.questionsService(
                    companyId
                ).companyEditorCreate(body);
                setSuccess('Question created successfully!');
                await resourcesStore.loadAllContent(companyId);
                navigate(`/content/question-editor/${id}`, { replace: true });
            }
        } catch (err) {
            setError(
                extractErrorMessage(err) ||
                    'Failed to save question. Please try again.'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAs = async (question) => {
        if (!companyId || !questionId) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        const body = buildEditorApiPayload(question);
        try {
            const { id } = await services.Companies.questionsService(
                companyId
            ).companyEditorSaveAs(questionId, body);
            setSuccess('Saved a new copy for your company.');
            await resourcesStore.loadAllContent(companyId);
            navigate(`/content/question-editor/${id}`, { replace: true });
        } catch (err) {
            setError(
                extractErrorMessage(err) ||
                    'Failed to save copy. Please try again.'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/content');
    };

    const handleBack = () => {
        navigate('/content');
    };

    const editorKey = useMemo(
        () => `${questionId || 'new'}-${loadVersion}`,
        [questionId, loadVersion]
    );

    if (!questionsEditorVisible) {
        navigate('/content');
        return null;
    }

    return (
        <Container fluid className="manager-content question-editor-page">
            <Helmet>
                <title>{isEditing ? 'Edit Question' : 'Create Question'}</title>
            </Helmet>

            <Block padding={24} gap={24}>
                <FlexDiv alignCenter gap={16}>
                    <ThemedButton
                        mode="secondary"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        text="Back to Content"
                    />
                    <Heading3>
                        {isEditing ? 'Edit Question' : 'Create New Question'}
                    </Heading3>
                </FlexDiv>

                {loadError && <Alert severity="error">{loadError}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                <Block
                    padding={16}
                    style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                    }}
                >
                    <TextDefault>
                        <strong>Instructions:</strong> Add a presentation with the
                        + under <strong>Presentation</strong> (pencil = edit). Add
                        steps with <strong>Add block</strong> under{' '}
                        <strong>Interactive</strong>; use arrows or the step row to
                        move between blocks. The <strong>properties drawer</strong>{' '}
                        on the right opens when you pick a block or edit the
                        presentation. Toolbar <strong>Preview</strong> shows a
                        learner-style pass. Layout matches the course flow:
                        presentation first, then interactive blocks in order.
                    </TextDefault>
                </Block>

                <div
                    style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        minHeight: '600px',
                    }}
                >
                    {isEditing &&
                    pageLoading &&
                    !initialQuestion &&
                    !loadError ? (
                        <Block padding={48}>
                            <ThemedSpinner />
                        </Block>
                    ) : (
                        <VisualEditor
                            key={editorKey}
                            initialQuestion={
                                isEditing ? initialQuestion : null
                            }
                            onSave={handleSave}
                            onSaveAs={handleSaveAs}
                            onCancel={handleCancel}
                            companyId={companyId}
                            companyUid={companyUid}
                            accountUid={accountUid}
                            primaryColor={primaryColor}
                            threatAreaOptions={
                                catalogStore.availableThreatAreas
                            }
                            threatAreasCatalog={catalogStore.threatAreaServerData}
                            renderPreviewModal={
                                companyId
                                    ? ({ question: q, companyId: cid, onClose }) => (
                                          <QuestionPreviewModal
                                              question={q}
                                              companyId={cid}
                                              onClose={onClose}
                                          />
                                      )
                                    : undefined
                            }
                        />
                    )}
                </div>

                {saving && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                        }}
                    >
                        <Block
                            padding={32}
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                textAlign: 'center',
                            }}
                        >
                            <TextDefault>Saving question…</TextDefault>
                        </Block>
                    </div>
                )}
            </Block>
        </Container>
    );
}

export default observer(QuestionEditor);
